import { cn } from '@/lib/utils';
import { UPLOAD_STATUSES, isMetadataExtractable } from './constants';

/**
 * Derive the display status and text from an upload's base status, extraction flag, and
 * whole-upload metadata-extraction counts (extractable/extracted successful file rows).
 * Never reports COMPLETED while `hasPending` is true — a file still pending means the
 * upload isn't done, regardless of how many of the files seen so far have metadata.
 */
export function deriveMetadataStatus({
  status,
  skipMetadataExtraction,
  hasFiles = false,
  hasPending = false,
  totalExtractable = 0,
  filesWithMetadata = 0,
}) {
  let displayStatus = status;
  let statusText =
    status === UPLOAD_STATUSES.ZIP_UPLOAD_COMPLETE ? 'ZIP Extracted' : status.replace('_', ' ');

  if (status === UPLOAD_STATUSES.ZIP_UPLOAD_COMPLETE && hasFiles && !hasPending) {
    if (skipMetadataExtraction || totalExtractable === 0) {
      displayStatus = UPLOAD_STATUSES.COMPLETED;
      statusText = 'completed';
    } else if (filesWithMetadata < totalExtractable) {
      displayStatus = UPLOAD_STATUSES.EXTRACTING_METADATA;
      statusText = `extracting metadata (${filesWithMetadata}/${totalExtractable})`;
    } else {
      displayStatus = UPLOAD_STATUSES.COMPLETED;
      statusText = 'completed';
    }
  }

  return { displayStatus, statusText };
}

/**
 * Get badge variant, className, and text for a file's metadata extraction status.
 */
export function getMetadataBadgeProps(file) {
  const variant = file.metadataExtracted
    ? 'default'
    : isMetadataExtractable(file)
      ? 'secondary'
      : 'outline';

  const className = cn(
    'text-xs pointer-events-none',
    file.metadataExtracted && 'bg-green-500 text-white',
  );

  const text = file.metadataExtracted
    ? 'Metadata Extracted'
    : isMetadataExtractable(file)
      ? 'Extracting Metadata...'
      : 'Not supported';

  return { variant, className, text };
}
